/**
 * Workspace date/time formatting. No React or DOM so Node tests can import it.
 * Display defaults to the workspace IANA zone (tenants.timezone). Users have no
 * timezone column, so there is no per-user override.
 */
import { calleeTimeZone } from '../../lib/business-hours.mjs';

export const EM_DASH = '\u2014';

const GLOBAL_KEY = '__ACL_WORKSPACE_TZ';
const DEFAULT_ZONE = 'America/Los_Angeles';

let workspaceTz = DEFAULT_ZONE;

const FRIENDLY_ZONES = {
  'America/Los_Angeles': ['Pacific Time', 'PT'],
  'America/Denver': ['Mountain Time', 'MT'],
  'America/Boise': ['Mountain Time', 'MT'],
  'America/Phoenix': ['Arizona Time', 'MST'],
  'America/Chicago': ['Central Time', 'CT'],
  'America/New_York': ['Eastern Time', 'ET'],
  'America/Detroit': ['Eastern Time', 'ET'],
  'America/Indiana/Indianapolis': ['Eastern Time', 'ET'],
  'America/Anchorage': ['Alaska Time', 'AKT'],
  'America/Juneau': ['Alaska Time', 'AKT'],
  'Pacific/Honolulu': ['Hawaii Time', 'HT'],
  UTC: ['UTC', 'UTC'],
  'Etc/UTC': ['UTC', 'UTC']
};

const GENERIC_ABBR = {
  PDT: 'PT', PST: 'PT',
  EDT: 'ET', EST: 'ET',
  CDT: 'CT', CST: 'CT',
  MDT: 'MT', MST: 'MT',
  AKDT: 'AKT', AKST: 'AKT',
  HST: 'HT', HDT: 'HT',
  GMT: 'UTC', UTC: 'UTC'
};

function readGlobalZone() {
  if (typeof globalThis === 'undefined') return '';
  const value = globalThis[GLOBAL_KEY];
  return value ? String(value) : '';
}

export function isValidTimeZone(timeZone) {
  const candidate = timeZone == null ? '' : String(timeZone).trim();
  if (!candidate) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: candidate });
    return true;
  } catch (e) {
    return false;
  }
}

export function setWorkspaceTimeZone(timeZone) {
  const candidate = timeZone == null ? '' : String(timeZone).trim();
  if (!isValidTimeZone(candidate)) return getWorkspaceTimeZone();
  workspaceTz = candidate;
  if (typeof globalThis !== 'undefined') globalThis[GLOBAL_KEY] = candidate;
  return candidate;
}

export function getWorkspaceTimeZone() {
  const globalZone = readGlobalZone();
  if (isValidTimeZone(globalZone)) return globalZone;
  return workspaceTz || DEFAULT_ZONE;
}

function resolveZone(timeZone) {
  if (timeZone != null && String(timeZone).trim() && isValidTimeZone(timeZone)) return String(timeZone).trim();
  return getWorkspaceTimeZone();
}

function spaces(text) {
  return String(text || '').replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * ISO, Postgres "YYYY-MM-DD HH:MM:SS+00", naive datetimes (UTC), epoch seconds or ms.
 * A date-only string is noon UTC so the calendar day does not shift in US zones.
 */
export function parseInstant(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    const ms = Math.abs(value) < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^-?\d+(\.\d+)?$/.test(raw)) return parseInstant(Number(raw));
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(raw + 'T12:00:00Z');
    return Number.isNaN(d.getTime()) ? null : d;
  }
  let s = raw.replace(/^(\d{4}-\d{2}-\d{2})[ T]/, '$1T');
  if (/[+-]\d{2}$/.test(s)) s += ':00';
  else if (/[+-]\d{4}$/.test(s)) s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(s)) s += 'Z';
  const t = Date.parse(s);
  if (Number.isNaN(t)) return null;
  return new Date(t);
}

function partMap(date, timeZone, options) {
  const fmt = new Intl.DateTimeFormat('en-US', Object.assign({ timeZone: timeZone }, options));
  const map = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  return map;
}

export function zonedYmd(value, timeZone) {
  const date = value instanceof Date && !Number.isNaN(value.getTime()) ? value : parseInstant(value);
  if (!date) return '';
  const zone = resolveZone(timeZone);
  try {
    const parts = partMap(date, zone, { year: 'numeric', month: '2-digit', day: '2-digit' });
    if (!parts.year || !parts.month || !parts.day) return '';
    return parts.year + '-' + parts.month + '-' + parts.day;
  } catch (e) {
    return '';
  }
}

export function todayYmd(timeZone) {
  return zonedYmd(new Date(), timeZone);
}

export function addDaysYmd(ymd, days) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!match) return '';
  const next = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + Number(days || 0)));
  if (Number.isNaN(next.getTime())) return '';
  return next.toISOString().slice(0, 10);
}

function friendlyPair(timeZone, date) {
  const known = FRIENDLY_ZONES[timeZone];
  if (known) return { name: known[0], abbr: known[1] };
  try {
    const longName = partMap(date, timeZone, { timeZoneName: 'long' }).timeZoneName || timeZone;
    const shortName = partMap(date, timeZone, { timeZoneName: 'short' }).timeZoneName || '';
    const name = longName.replace(' Daylight', '').replace(' Standard', '');
    const abbr = GENERIC_ABBR[shortName] || shortName || name;
    return { name: name, abbr: abbr };
  } catch (e) {
    return { name: timeZone, abbr: '' };
  }
}

export function zoneAbbr(timeZone, at) {
  const zone = resolveZone(timeZone);
  const date = at instanceof Date ? at : new Date();
  return friendlyPair(zone, date).abbr || '';
}

export function zoneLabel(timeZone, at) {
  const zone = resolveZone(timeZone);
  const date = at instanceof Date ? at : new Date();
  const pair = friendlyPair(zone, date);
  if (!pair.name) return zone;
  if (!pair.abbr || pair.abbr === pair.name) return pair.name;
  return pair.name + ' (' + pair.abbr + ')';
}

function offsetAbbr(date, timeZone) {
  try {
    return partMap(date, timeZone, { timeZoneName: 'short' }).timeZoneName || '';
  } catch (e) {
    return '';
  }
}

function clock(date, timeZone) {
  const parts = partMap(date, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true });
  const period = String(parts.dayPeriod || '').replace(/\./g, '').toUpperCase();
  return parts.hour + ':' + parts.minute + (period ? ' ' + period : '');
}

function monthDay(date, timeZone, withYear) {
  const parts = partMap(date, timeZone, {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined
  });
  if (withYear) return parts.month + ' ' + parts.day + ', ' + parts.year;
  return parts.month + ' ' + parts.day;
}

export function formatDateTime(value, options) {
  const opts = options || {};
  const date = parseInstant(value);
  if (!date) return EM_DASH;
  const zone = resolveZone(opts.timeZone);
  const style = opts.style || 'smart';
  try {
    const time = clock(date, zone);
    if (style === 'time') return spaces(time);
    if (style === 'date') return spaces(monthDay(date, zone, true));
    const ymd = zonedYmd(date, zone);
    const today = todayYmd(zone);
    const abbr = offsetAbbr(date, zone);
    if (style === 'full') return spaces(monthDay(date, zone, true) + ', ' + time + (abbr ? ' ' + abbr : ''));
    const sameYear = ymd.slice(0, 4) === today.slice(0, 4);
    if (style === 'short') {
      return spaces((sameYear ? monthDay(date, zone, false) : monthDay(date, zone, true)) + ', ' + time);
    }
    if (ymd && ymd === today) return spaces('Today ' + time);
    if (ymd && ymd === addDaysYmd(today, -1)) return spaces('Yesterday ' + time);
    if (sameYear) return spaces(monthDay(date, zone, false) + ', ' + time);
    return spaces(monthDay(date, zone, true) + ', ' + time + (abbr ? ' ' + abbr : ''));
  } catch (e) {
    return EM_DASH;
  }
}

function relativePhrase(count, unit, future) {
  const n = Math.max(1, count);
  if (unit === 'min') return future ? 'in ' + n + ' min' : n + ' min ago';
  const label = n === 1 ? unit : unit + 's';
  return future ? 'in ' + n + ' ' + label : n + ' ' + label + ' ago';
}

export function formatRelative(value, now) {
  const date = parseInstant(value);
  if (!date) return EM_DASH;
  const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const delta = base.getTime() - date.getTime();
  const abs = Math.abs(delta);
  const future = delta < 0;
  if (Math.round(abs / 1000) < 45) return 'Just now';
  const minutes = Math.round(abs / 60000);
  if (minutes < 60) return relativePhrase(minutes, 'min', future);
  const hours = Math.round(abs / 3600000);
  if (hours < 24) return relativePhrase(hours, 'hour', future);
  const days = Math.round(abs / 86400000);
  if (days < 14) return relativePhrase(days, 'day', future);
  return formatDateTime(date, { style: 'date' });
}

export function formatDuration(value) {
  if (value == null || value === '') return EM_DASH;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return EM_DASH;
  const total = Math.round(n);
  if (total >= 3600) {
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return hours + 'h ' + String(minutes).padStart(2, '0') + 'm';
  }
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes + ':' + String(seconds).padStart(2, '0');
}

export function leadLocalTimeLabel(lead, now) {
  if (!lead || typeof lead !== 'object') return '';
  const zone = lead.local_timezone || calleeTimeZone({
    phone: lead.phone || lead.phone_e164,
    state: lead.state
  });
  if (!zone) return 'Local time for lead: unknown';
  const date = now instanceof Date ? now : new Date();
  const time = formatDateTime(date, { timeZone: zone, style: 'time' });
  if (!time || time === EM_DASH) return 'Local time for lead: unknown';
  const abbr = zoneAbbr(zone, date);
  return 'Local time for lead: ' + time + (abbr ? ' ' + abbr : '');
}

export { calleeTimeZone };
